import React, { useEffect, useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Table from '../../../components/Table';
import {
  Grid,
  styled,
  TextField,
  Paper,
  TableRow,
  TableCell,
  Button,
  Typography,
  InputLabel,
  FormControl,
  MenuItem,
  InputAdornment,
  Select
} from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CompanyCreate } from '../../../schema/company';
import PageHeader from '../../../components/PageHeader';
import { api } from '../../../api';
import { Person, PersonType } from '../../../schema/person';
import { TableResponse } from '../../../schema/helper';
import SearchIcon from '@mui/icons-material/Search';
import MultiSelect from '../../../components/MultiSelect';

const personHead = [
  'เลขบัตรประชาชน',
  'ชื่อ นามสกุล',
  'ประเภท',
  'บริษัท',
  'ผู้คุมงาน',
  'พื้นที่ทั่วไป',
  'พื้นที่ควบคุม'
];

const CompanyDetail: React.FC = () => {
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const { company_id } = useParams();
  const [form, setForm] = useState<CompanyCreate>({
    company_code: '',
    company_name: '',
    company_name_en: '',
    company_type_id: '',
    company_id: '',
    phone_number: '',
    email: '',
    address: ''
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [persons, setPersons] = useState<TableResponse<Person> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[] | number[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('');

  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    api.get<PersonType[]>(`/person-type`).then((res) => {
      const { data } = res;
      setPersonTypes(data);
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const timeOutId = setTimeout(() => {
      if (!company_id) {
        return;
      }
      const searchParams = new URLSearchParams();
      searchParams.append('company_id', `${company_id}`);

      if (filterID) {
        searchParams.set('personnal_id', `${filterID}`);
      }
      if (filterName) {
        searchParams.set('name', `${filterName}`);
      }
      if (filterPersonTypeIDList.length) {
        filterPersonTypeIDList.forEach((person_type_id) => {
          searchParams.append('person_type_id', `${person_type_id}`);
        });
      }

      if (filterStatus) {
        if (filterStatus == 'active') {
          searchParams.set('is_active', `${true}`);
        } else if (filterStatus == 'inactive') {
          searchParams.set('is_active', `${false}`);
        }
      }

      searchParams.set('offset', `${page * rowsPerPage}`);
      searchParams.set('limit', `${rowsPerPage}`);
      api
        .get<TableResponse<Person>>(`/person/table`, { params: searchParams })
        .then((res) => {
          const { data } = res;
          setPersons(data);
        })
        .finally(() => setIsLoading(false));
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [filterID, filterStatus, filterName, filterPersonTypeIDList, page, rowsPerPage]);

  const handleExportCSVFile = () => {
    if (isExportLoading) {
      return;
    }
    const searchParams = new URLSearchParams();
    searchParams.set('company_id', `${company_id}`);
    if (filterID) {
      searchParams.set('personnal_id', `${filterID}`);
    }
    if (filterName) {
      searchParams.set('name', `${filterName}`);
    }
    if (filterPersonTypeIDList.length) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }

    if (filterStatus) {
      if (filterStatus == 'active') {
        searchParams.set('is_active', `${true}`);
      } else if (filterStatus == 'inactive') {
        searchParams.set('is_active', `${false}`);
      }
    }
    setIsExportLoading(true);
    api
      .get(`/person/export`, {
        params: searchParams,
        responseType: 'blob'
      })
      .then((response) => {
        const today = new Date();
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          'company_' +
            company_id +
            '_person_export_data_' +
            today.toLocaleDateString('en-US') +
            '.xlsx'
        );
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsExportLoading(false));
  };

  useEffect(() => {
    api.get<CompanyCreate>(`/company/${company_id}`).then((res) => {
      const { data } = res;
      setForm(data);
    });
  }, []);

  return (
    <RootContainer>
      <PageHeader text="ข้อมูลบริษัท" />
      <Button component={Link} to="/company-management" sx={{ my: 3, p: 0 }}>
        <ArrowBackIosIcon />
        จัดการข้อมูลบริษัท
      </Button>

      <Paper>
        <Typography variant="h6" sx={{ p: 2 }}>
          ข้อมูลบริษัท
        </Typography>
        <Form sx={{ padding: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="ชื่อบริษัท"
                  variant="outlined"
                  placeholder="ชื่อบริษัท"
                  name="company_name"
                  value={form.company_name}
                  autoComplete="off"
                  disabled={!form.company_name}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="ชื่อบริษัท (ภาษาอังกฤษ)"
                  variant="outlined"
                  placeholder="ชื่อบริษัท (ภาษาอังกฤษ)"
                  name="company_name_en"
                  value={form.company_name_en}
                  autoComplete="off"
                  disabled={!form.company_name_en}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="รหัสบริษัท"
                  variant="outlined"
                  placeholder="รหัสบริษัท"
                  name="company_code"
                  value={form.company_code}
                  autoComplete="off"
                  disabled={!form.company_code}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="หมายเลขผู้เสียภาษี"
                  variant="outlined"
                  placeholder="หมายเลขผู้เสียภาษี"
                  name="company_id"
                  value={form.company_id}
                  autoComplete="off"
                  disabled={!form.company_id}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="company-type">ประเภทบริษัท</InputLabel>
                  <Select
                    labelId="company-type"
                    label="ประเภทบริษัท"
                    name="company_type_id"
                    variant="outlined"
                    value={form.company_type_id}
                    autoComplete="off"
                    disabled={!form.company_type_id}
                  >
                    <MenuItem value="2">ประจำ</MenuItem>
                    <MenuItem value="3">ชั่วคราว</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="ที่อยู่"
                  variant="outlined"
                  placeholder="ที่อยู่"
                  name="address"
                  value={form.address}
                  autoComplete="off"
                  disabled={!form.address}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="เบอร์โทรศัพท์"
                  variant="outlined"
                  placeholder="เบอร์โทรศัพท์"
                  name="phone_number"
                  value={form.phone_number}
                  autoComplete="off"
                  disabled={!form.phone_number}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="อีเมล"
                  variant="outlined"
                  placeholder="อีเมล"
                  name="email"
                  value={form.email}
                  autoComplete="off"
                  disabled={!form.email}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Form>
      </Paper>
      <Paper sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ px: 2, py: 2 }}>
          {'บุคคลที่อยู่ในบริษัท'}
          <Button
            sx={{ ml: 2 }}
            variant="outlined"
            color="primary"
            onClick={handleExportCSVFile}
            disabled={isExportLoading}
          >
            Export
          </Button>
        </Typography>
        <Grid container spacing={2} sx={{ padding: 2 }}>
          <Grid item md={2}>
            <TextField
              id="search-text-label"
              label="เลขบัตรประชาชน"
              variant="outlined"
              placeholder="เลขบัตรประชาชน"
              onChange={(event) => setFilterID(event.target.value)}
              fullWidth
              value={filterID}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              autoComplete="off"
            />
          </Grid>
          <Grid item md={2}>
            <TextField
              id="search-text-label"
              label="ค้นหาชื่อ - นามสกุล"
              variant="outlined"
              placeholder="ค้นหาชื่อ - นามสกุล"
              value={filterName}
              onChange={(event) => setFilterName(event.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item md={2}>
            <MultiSelect
              options={personTypes.map((data) => ({
                id: data.id.toString(),
                name: data.type_name
              }))}
              onChange={(data) => setFilterPersonTypeIDList(data)}
              label="ประเภทบุคคล"
            />
          </Grid>
          <Grid item md={2}>
            <FormControl fullWidth>
              <InputLabel id="select-status-label">สถานะ</InputLabel>
              <Select
                labelId="select-status-label"
                value={filterStatus}
                label="สถานะ"
                name="status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">ทั้งหมด</MenuItem>
                <MenuItem value="active">อนุญาตแลกบัตร</MenuItem>
                <MenuItem value="inactive">ไม่อนุญาตแลกบัตร</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ padding: 2 }}>
          <Grid item xs={12}>
            <Table
              heads={personHead}
              count={persons?.total ? persons?.total : 0}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              isLoading={isLoading}
            >
              {persons && persons.data.length <= 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ p: 1 }}>
                    <Typography>ไม่พบข้อมูลรายการบุคคล</Typography>
                  </TableCell>
                </TableRow>
              )}
              {persons?.data.map((data, index) => (
                <TableRow key={index}>
                  <TableCell align="center">{data.personnal_id}</TableCell>
                  <TableCell align="center">{data.full_name}</TableCell>
                  <TableCell align="center">{data.person_type.type_name}</TableCell>
                  <TableCell align="center">{data.company.company_name}</TableCell>
                  <TableCell align="center">{data.supervisor?.full_name}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color={data.is_active ? 'success.main' : 'error'}>
                      {data.is_active
                        ? data.is_temp_active
                          ? 'อนุญาตโดยเจ้าหน้าที่'
                          : 'อนุญาตโดยระบบ'
                        : 'ไม่อนุญาตแลกบัตร'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      color={data.is_control_area_active ? 'success.main' : 'error'}
                    >
                      {data.is_control_area_active
                        ? data.is_control_area_temp_active
                          ? 'อนุญาตโดยเจ้าหน้าที่'
                          : 'อนุญาตโดยระบบ'
                        : 'ไม่อนุญาตแลกบัตร'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </Grid>
        </Grid>
      </Paper>
    </RootContainer>
  );
};

export default CompanyDetail;

const RootContainer = styled('div')`
  padding: 24px;
`;

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
